import { useQueryState } from 'nuqs';
import { useEffect, useMemo, useState } from 'react';
import { FilterMap, Filters, Platform } from '~/screens/MyAgency/types';
import { getStringFromFilters } from '~/screens/utils/url';

import { AdvancedFilters } from '../store';

const VALID_PLATFORMS: Platform[] = [
  'instagram',
  'tiktok',
  'youtube',
  'snapchat',
];

const VALID_FILTER_TYPES: Record<Platform, string[]> = {
  instagram: [
    'gender',
    'genderCriteria',
    'age',
    'ageCriteria',
    'country',
    'countryCriteria',
    'followers',
    'avgFeedReachEngRate',
    'avgFeedReach',
    'avgStoryReach',
    'avgReelsViews',
  ],
  tiktok: [
    'gender',
    'genderCriteria',
    'age',
    'ageCriteria',
    'country',
    'countryCriteria',
    'followers',
    'avgViews',
    'avgEngRate',
  ],
  youtube: [
    'gender',
    'genderCriteria',
    'age',
    'ageCriteria',
    'country',
    'countryCriteria',
    'followers',
    'avgViews',
    'avgEngRate',
  ],
  snapchat: [
    'gender',
    'genderCriteria',
    'age',
    'ageCriteria',
    'country',
    'countryCriteria',
    'followers',
  ],
};

const VALID_GENDER_VALUES = ['Male', 'Female', 'Other', 'Unknown'];
const VALID_AGE_VALUES = {
  instagram: ['13-17', '18-24', '25-34', '35-65+'],
  tiktok: ['18-24', '25-34', '35-44', '45-54', '55+'],
  youtube: ['13-24', '25-34', '35+'],
  snapchat: ['13-17', '18-20', '21-24', '25-34', '35+'],
};

const isValidPlatform = (platform: string): platform is Platform => {
  return VALID_PLATFORMS.includes(platform as Platform);
};

const isValidFilterType = (platform: Platform, filterType: string): boolean => {
  return VALID_FILTER_TYPES[platform]?.includes(filterType) || false;
};

const isValidFilterValue = (
  platform: Platform,
  filterType: string,
  value: string,
): boolean => {
  if (filterType === 'gender') {
    return VALID_GENDER_VALUES.includes(value);
  }

  if (filterType === 'age') {
    return VALID_AGE_VALUES[platform]?.includes(value) || false;
  }

  if (filterType === 'country') {
    return typeof value === 'string' && value.length > 0;
  }

  if (filterType.includes('Criteria')) {
    const numValue = Number(value);
    return !isNaN(numValue) && numValue >= 0 && numValue <= 100;
  }

  if (filterType === 'followers' || filterType.startsWith('avg')) {
    const numValue = Number(value);
    return !isNaN(numValue) && numValue >= 0;
  }

  return true;
};

const validateAndCleanFilters = (filters: string[]): string[] => {
  return filters.filter((filter) => {
    const parts = filter.split(':');
    if (parts.length !== 3) return false;

    const [platform, filterType, value] = parts;

    return (
      isValidPlatform(platform) &&
      isValidFilterType(platform as Platform, filterType) &&
      isValidFilterValue(platform as Platform, filterType, value)
    );
  });
};

export const useURLFilters = () => {
  const [urlFilter, setUrlFilter] = useQueryState('filters');

  const [activePlatformUrl, setActivePlatformUrl] =
    useQueryState<Platform | null>('audiencePlatform', {
      defaultValue: null,
      parse: (value) => value as Platform | null,
    });

  /** appliedFiltersMap holds a Map() of applied filters */
  const [appliedFiltersMap, setAppliedFiltersMap] = useState<
    Map<string, string>
  >(new Map());

  useEffect(() => {
    if (!urlFilter) {
      setAppliedFiltersMap(new Map());
      return;
    }

    try {
      const decodedFilters = Buffer.from(urlFilter, 'base64').toString('utf-8');
      const filters = decodedFilters.split(',').filter(Boolean);
      const validFilters = validateAndCleanFilters(filters);

      if (validFilters.length !== filters.length) {
        const newFiltersMap = new Map<string, string>();
        validFilters.forEach((filter) => {
          const [platform, filterType, value] = filter.split(':');
          const key = `${platform}:${String(filterType)}`;
          newFiltersMap.set(key, value);
        });

        if (newFiltersMap.size === 0) {
          setUrlFilter(null);
          setAppliedFiltersMap(new Map());
        } else {
          const newUrlFilter = getStringFromFilters(newFiltersMap);
          setUrlFilter(newUrlFilter);
          setAppliedFiltersMap(newFiltersMap);
        }
        return;
      }

      const newFilters = new Map<string, string>();
      validFilters.forEach((filter) => {
        const [platform, filterType, value] = filter.split(':');
        const key = `${platform}:${String(filterType)}`;
        newFilters.set(key, value);
      });

      setAppliedFiltersMap(newFilters);
    } catch (error) {
      setUrlFilter(null);
      setAppliedFiltersMap(new Map());
    }
  }, [urlFilter, setUrlFilter]);

  // Convert the Map of filters into an array of filter objects
  const appliedFilters: AdvancedFilters['appliedFilters'] = useMemo(() => {
    const result: Filters[] = [];

    appliedFiltersMap.forEach((value, key) => {
      const [platform, filterType] = key.split(':');
      if (
        isValidPlatform(platform) &&
        isValidFilterType(platform, filterType)
      ) {
        result.push({
          platform: platform as Platform,
          filterType: filterType as keyof FilterMap[Platform],
          value,
        });
      }
    });

    return result;
  }, [appliedFiltersMap]);

  const getFilter: AdvancedFilters['getFilter'] = <
    P extends Platform,
    K extends keyof FilterMap[P],
  >(
    platform: P,
    filterType: K,
  ) => {
    const key = `${platform}:${String(filterType)}`;
    return appliedFiltersMap.get(key) as FilterMap[P][K] | undefined;
  };

  const setFilter: AdvancedFilters['setFilter'] = (
    platform,
    filterType,
    value,
  ) => {
    if (
      !isValidPlatform(platform as string) ||
      !isValidFilterType(platform, String(filterType))
    ) {
      return;
    }

    const key = `${platform}:${String(filterType)}`;
    const newFilters = new Map(appliedFiltersMap);
    newFilters.set(key, value as string);

    setUrlFilter(getStringFromFilters(newFilters));
    setAppliedFiltersMap(newFilters);
  };

  const clearFilters: AdvancedFilters['clearFilters'] = (
    platform,
    filterTypes,
  ) => {
    const newFilters = new Map(appliedFiltersMap);

    if (!platform && !filterTypes) {
      newFilters.clear();
      setActivePlatformUrl(null);
    } else if (platform && !filterTypes) {
      Array.from(newFilters.keys()).forEach((key) => {
        if (key.startsWith(platform)) {
          newFilters.delete(key);
        }
      });
      setActivePlatformUrl(null);
    } else if (platform && filterTypes) {
      filterTypes.forEach((filterType) => {
        const key = `${platform}:${String(filterType)}`;
        newFilters.delete(key);
      });
    }

    setUrlFilter(getStringFromFilters(newFilters));
    setAppliedFiltersMap(newFilters);
  };

  return {
    appliedFiltersMap,
    setAppliedFiltersMap,
    appliedFilters,
    getFilter,
    setFilter,
    clearFilters,
    activePlatformUrl,
    setActivePlatformUrl,
  };
};
